import * as React from 'react'
import cx from 'classnames'
import { useField } from 'formik'
import { TestIdentifiable } from '@toasttab/buffet-shared-types'
import {
  CardSelector,
  CardSelectorProps,
  CardSelectorItem
} from '@toasttab/buffet-pui-card-selector'
import { FormValuesWithName, TypedName } from '../commonTypes'
import {
  SelectionGroup,
  SelectionGroupProps
} from '@toasttab/buffet-pui-input-base'
import {
  HelperTextProps,
  LabeledHelperTextProps
} from '@toasttab/buffet-pui-text-base'

const getTestId = (testId: string | number, suffix: string | number) =>
  `${testId}-${suffix}`

export interface CardSelectorFieldProps<
  FormValues extends FormValuesWithName = string
> extends Omit<CardSelectorProps, 'name'>,
    TypedName<FormValues> {}

/**
 * A single CardSelector connected to a Formik form.
 */
export const CardSelectorField = <
  FormValues extends FormValuesWithName = string
>(
  props: CardSelectorFieldProps<FormValues>
) => {
  const [field] = useField({
    ...props,
    size: undefined,
    type: props.multiple ? 'checkbox' : 'radio'
  })

  return (
    <CardSelector
      {...field}
      {...props}
      onChange={(event) => {
        props.onChange?.(event)
        field.onChange(event)
      }}
      onBlur={(event) => {
        props.onBlur?.(event)
        field.onBlur(event)
      }}
    />
  )
}

export interface CardSelectorGroupFieldProps<
  FormValues extends FormValuesWithName = string
> extends Omit<SelectionGroupProps, 'children' | 'name'>,
    TestIdentifiable,
    HelperTextProps,
    Omit<LabeledHelperTextProps, 'name'>,
    TypedName<FormValues> {
  /** CSS classes to apply to the actual CardSelectors (label) */
  /** @deprecated: This will be removed in a future version. Please use default card styling. */
  className?: string
  /** CSS classes to apply to the options (li) */
  /** @deprecated: This will be removed in a future version. Also, it will be ignored if you use the `itemsContainerClassName` prop. */
  optionClassName?: string
  /** CSS classes to apply to the container (fieldset) */
  containerClassName?: string
  /** CSS classes to apply to options container (ul) */
  /** @deprecated: This will be removed in a future version. Please use `itemsContainerClassName`. */
  optionsContainerClassName?: string
  /** True for multi-select (checkbox), false for single select (radio) */
  multiple?: CardSelectorProps['multiple']
  /** Are the selectable items disabled? */
  disabled?: boolean
  /** True for hiding multi-select checkboxes, false for displaying them */
  hideMultiSelectCheckboxes?: boolean
  /** Array of options used to generate the underlying CardSelectors */
  options: CardSelectorItem[]
}

/**
 * A component for rendering a group of CardSelectors in a Formik form
 * as either a single-select (radio group) or multi-select (checkbox group).
 */
export const CardSelectorGroupField = <
  FormValues extends FormValuesWithName = string
>(
  props: CardSelectorGroupFieldProps<FormValues>
) => {
  const {
    options = [],
    name,
    multiple,
    hideMultiSelectCheckboxes = false,
    testId = 'card-selector-group',
    // eslint-disable-next-line
    className,
    disabled,
    // eslint-disable-next-line
    optionClassName,
    containerClassName,
    // eslint-disable-next-line
    optionsContainerClassName,
    itemsContainerClassName,
    ...restProps
  } = props

  const derivedDisabled = options.every((option) => option.disabled) || disabled
  const [, { error, touched }] = useField({
    name,
    size: undefined,
    type: multiple ? 'checkbox' : 'radio'
  })
  const invalid = touched && !!error
  const hasDeprecatedClassNames =
    !!optionClassName || !!optionsContainerClassName

  return (
    <SelectionGroup
      disabled={derivedDisabled}
      multiple={multiple}
      className={containerClassName}
      {...restProps}
      itemsContainerClassName={cx('my-4 grid gap-3', itemsContainerClassName)}
      testId={testId}
      invalid={invalid}
      errorText={error}
    >
      {hasDeprecatedClassNames ? (
        <ul
          className={cx(optionsContainerClassName || 'flex flex-col space-y-4')}
        >
          {options.map((op) => {
            const { disabled: optionIsDisabled, ...restOpProps } = op

            return (
              <li className={optionClassName} key={op.value}>
                <CardSelectorField
                  name={name}
                  disabled={op.disabled || disabled}
                  multiple={multiple}
                  hideMultiSelectCheckboxes={hideMultiSelectCheckboxes}
                  testId={getTestId(testId, op.value)}
                  className={className}
                  {...restOpProps}
                >
                  {op.contents}
                </CardSelectorField>
              </li>
            )
          })}
        </ul>
      ) : (
        options.map((op) => {
          const { disabled: optionIsDisabled, ...restOpProps } = op

          return (
            <CardSelectorField
              name={name}
              disabled={op.disabled || disabled}
              multiple={multiple}
              hideMultiSelectCheckboxes={hideMultiSelectCheckboxes}
              testId={getTestId(testId, op.value)}
              className={className}
              key={op.value}
              {...restOpProps}
            >
              {op.contents}
            </CardSelectorField>
          )
        })
      )}
    </SelectionGroup>
  )
}
